// The recipe's weights, as an order rather than four numbers.
//
// "How much does dryness matter, on a scale of 0 to 100" is a question almost
// nobody can answer. "Does dryness matter more than calm" is one everybody can.
// So the editor asks the second question — drag four chips into the order that
// matters to you — and this module is the translation back to the first, which
// is the only form recipe.ts, the ?r= wire string and the server's cache key
// have ever known.
//
// That is the whole reason this is a separate file. lib/recipe.ts is the canon:
// its schema, its clamping and its encoding are what the URL and the server
// agree on, and a UI convenience has no business inside it. Ranking is a view
// onto weights, so it lives beside them rather than in them.

import type { WeightKey, Weights } from './recipe.ts';

// What each rank is worth. Multiples of 5, because clampRecipe snaps weights to
// 5s — a value like 47 would silently become 45 and the ranking would stop
// round-tripping. The gaps are uneven on purpose: first place should outweigh
// fourth by a lot more than second outweighs third, or "in order" would mean
// almost nothing to the score.
export const RANK_WEIGHTS = [70, 55, 40, 20] as const;

// The order weights are compared in when two are equal. Fixed, so a tie never
// renders one way on this paint and the other way on the next.
const TIEBREAK: WeightKey[] = ['dry', 'calm', 'sunny', 'clear', 'warm'];

// An order of keys → the weights object recipe.ts takes.
//
// `base` is the recipe's current weights, and everything not in `order` keeps
// its value: exactly one of `warm`/`calm` contradicts the gate and is forced to
// zero by clampRecipe, so the editor only ever ranks four of the five. Passing
// the other one through untouched keeps this from being the thing that decides
// its fate — clampRecipe still does.
export function weightsFromRanks(order: WeightKey[], base: Weights): Weights {
  const out = { ...base };
  order.forEach((k, i) => {
    out[k] = RANK_WEIGHTS[Math.min(i, RANK_WEIGHTS.length - 1)];
  });
  return out;
}

// The weights of an arbitrary recipe → the order to draw them in.
//
// The load-bearing half. A recipe arriving from a ?r= link, from localStorage
// or from a preset carries whatever numbers its author chose, not the four this
// module would have produced — so the ranking has to be derived from them
// rather than assumed, or a shared link would render in the wrong order.
export function ranksFromWeights(w: Weights, visible: WeightKey[]): WeightKey[] {
  return [...visible].sort((a, b) => {
    const d = (w[b] ?? 0) - (w[a] ?? 0);
    if (d !== 0) return d;
    return TIEBREAK.indexOf(a) - TIEBREAK.indexOf(b);
  });
}
